import { Footer } from "@components/Footer";
import { Input } from "@components/Input";
import { Loading } from "@components/Loading";
import { TopNav } from "@components/TopNav";
import { useTranslation } from "@helpers/useTranslation";
import { doc, updateDoc } from "firebase/firestore";
import { FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FirebaseContext } from "src/helpers/firebase";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useTeam } from "src/helpers/useTeam";
import { useToast } from "src/helpers/useToast";

export const TeamSettings: FC = () => {
  const { t } = useTranslation("teamsettings");
  const { firestore } = useContext(FirebaseContext);

  const [team, loading] = useTeam();
  const [event, loadingEvent, errorEvent] = useEvent();
  const [updating, setUpdating] = useState(false);
  const eventId = useEventId();
  const toast = useToast();

  useEffect(() => {
    if (errorEvent) console.error(errorEvent);
  }, [errorEvent]);

  const isPromoEvent = event?.isPromo ?? false;

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      contact: true,
    },
  });

  useEffect(() => {
    if (team) {
      const { name, email, password, contact } = team;
      reset({ name, email, password, contact });
    }
  }, [team]);

  const onSubmit = async ({ name, email, password, contact }) => {
    try {
      setUpdating(true);
      const docRef = doc(firestore, `events/${eventId}/teams/${team.id}`);
      await updateDoc(docRef, { name, email, password, contact });
      setUpdating(false);
      toast(t("teamSettingsUpdated"), "✅");
    } catch (error) {
      console.error(`Problem occured updating team ${team.id} in event ${eventId}, error: ${error}`);
      toast(t("somethingWentWrong"), "🤷‍♂️", "error");
    }
  };

  const isPlayers = event?.mainConfig?.playersOnly ?? false;
  const title = isPlayers ? t("titlePlayers") : t("title");
  const name = isPlayers ? t("playerName") : t("teamName");
  const nameTooltip = isPlayers ? t("playerNameTooltip") : t("teamNameTooltip");
  const password = isPlayers ? t("passwordPlayers") : t("passwordTeams");
  const passwordTooltip = isPlayers ? t("passwordPlayersTooltip") : t("passwordTeamsTooltip");

  return (
    <div className="h-screen">
      <main>
        <TopNav showBack />
        <Loading loading={!team || !event || loading || loadingEvent}>
          <h1 className="mt-16 mb-8 w-full text-center  text-4xl font-bold">{title}</h1>
          <div className="px-8">
            <form onSubmit={handleSubmit(onSubmit)} className="h-100 flex w-full flex-col justify-center">
              <div className="align-center mx-auto mb-4 flex w-full max-w-sm">
                <Input
                  label={name}
                  name="name"
                  type="name"
                  disabled
                  requiredMessage={t("fieldRequired")}
                  errors={errors}
                  register={register}
                  tooltip={nameTooltip}
                />
              </div>
              {/* {isPromoEvent && (
                <div className="align-center mx-auto mb-4 flex w-full max-w-sm">
                  <Input
                    label={t("email")}
                    name="email"
                    required
                    placeholder={t("emailExample")}
                    type="email"
                    requiredMessage={t("fieldRequired")}
                    errors={errors}
                    register={register}
                    autoComplete="on"
                  />
                </div>
              )} */}

              <div className="align-center mx-auto mb-12 flex w-full max-w-sm">
                <Input
                  label={password}
                  name="password"
                  type="password"
                  placeholder={t("passwordPlaceholder")}
                  requiredMessage={t("fieldRequired")}
                  errors={errors}
                  register={register}
                  tooltip={passwordTooltip}
                />
              </div>
              <div className="align-center mx-auto mb-12 flex w-full max-w-sm justify-center">
                <button
                  type="submit"
                  disabled={loading || updating}
                  className={`${loading ? "loading" : ""} btn-primary btn mb-4 max-w-sm`}
                >
                  {t("update")}
                </button>
              </div>
            </form>
          </div>
        </Loading>
      </main>
      <Footer />
    </div>
  );
};
